import {BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {LogInType} from 'app/types/Shared';
import {RootState} from '../store';

/**
 * Extracts organization type from the ID token claims.
 *
 * Checks if user's groups contain the internal Takeda group IDs.
 * If user is in internal group IDs, they are classified as Internal (Takeda).
 * Otherwise, they are classified as External (CRO).
 */
export const extractOrganizationFromToken = (claims: Record<string, unknown>): LogInType => {
  console.log('[extractOrganizationFromToken] ===== CALLED ===== with claims:', claims);

  // Parse internal group IDs from environment variable (comma-separated)
  const internalGroupIdsEnv = process.env.REACT_APP_ENTRA_INTERNAL_GROUP_IDS || '';
  console.log('[extractOrganizationFromToken] RAW ENV REACT_APP_ENTRA_INTERNAL_GROUP_IDS:', internalGroupIdsEnv);

  const internalGroupIds = internalGroupIdsEnv
    .split(',')
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean);

  console.log('[extractOrganizationFromToken] Parsed internal group IDs:', internalGroupIds);
  console.log('[extractOrganizationFromToken] Number of configured groups:', internalGroupIds.length);

  if (internalGroupIds.length === 0) {
    console.warn(
      '[extractOrganizationFromToken] WARNING: REACT_APP_ENTRA_INTERNAL_GROUP_IDS is not configured; ' +
        'all authenticated users will be treated as External.',
    );
    return LogInType.External;
  }

  // Extract groups claim from token (array of group object IDs)
  const userGroups = Array.isArray(claims.groups)
    ? (claims.groups as string[]).map((g) => String(g).toLowerCase())
    : [];
  console.log('[extractOrganizationFromToken] User groups from token:', userGroups);
  console.log('[extractOrganizationFromToken] Number of user groups:', userGroups.length);

  // Check if user belongs to any internal group
  const isInternal = internalGroupIds.some((groupId) => {
    const match = userGroups.includes(groupId);
    console.log(`[extractOrganizationFromToken] Checking group "${groupId}" against user groups: ${match}`);
    return match;
  });

  const userType = isInternal ? LogInType.Internal : LogInType.External;
  console.log('[extractOrganizationFromToken] ===== RESULT: detected userType:', userType, '=====');

  return userType;
};

/**
 * Determines whether a user is an internal Takeda member or an external/CRO guest
 * by inspecting the decoded ID token claims.
 *
 * Key differentiator: the `idp` claim is present for B2B guest (external) users
 * because their home identity provider is a different tenant. Internal Takeda members
 * authenticate directly against the Takeda tenant and have no `idp` claim.
 *
 * @deprecated Use extractOrganizationFromToken instead for group-based validation
 */
export const determineUserTypeFromClaims = (claims: Record<string, unknown>): LogInType => {
  const idp = claims.idp;
  console.log('[determineUserTypeFromClaims] idp claim value:', idp);

  // idp is null or undefined for internal Takeda members;
  // it holds the home-tenant URL for external/CRO B2B guest accounts.
  const userType = idp !== null && idp !== undefined && idp !== '' ? LogInType.External : LogInType.Internal;
  console.log('[determineUserTypeFromClaims] detected userType:', userType);
  return userType;
};

const decodeToken = (token: string): Record<string, unknown> => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid token');
  }
  return JSON.parse(atob(parts[1]));
};

const processToken = (token: string) => {
  const decodedToken = decodeToken(token);
  const tokenVersion = decodedToken.ver; // Token version claim

  // Validate token version - reject v1 tokens
  if (tokenVersion !== '2.0') {
    console.error(`Invalid token version detected: ${tokenVersion}. Expected v2.0 token.`);
    throw new Error(`Token version ${tokenVersion} not supported. Please use v2.0 tokens.`);
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASEURL || 'http://localhost:3500',
  prepareHeaders: (headers, {getState}) => {
    const state = getState() as RootState;
    const authToken = state.auth.token;

    if (authToken && authToken.idToken) {
      // Validate token version
      processToken(authToken.idToken);

      // Send Authorization header for authentication
      // Backend validates token is present but does NOT decode it
      headers.set('Authorization', `Bearer ${authToken.idToken}`);

      // Send RequesterType header for content filtering
      // Backend uses this to determine if user is internal (Takeda) or external (CRO)
      const organization = state.user.user?.organization;
      const userType = state.user.userType;
      const requesterType = organization ?? userType;
      console.log('[auth.baseQuery] RequesterType resolution');
      console.log('  ΓåÆ organization:', organization);
      console.log('  ΓåÆ userType:', userType);
      console.log('  ΓåÆ resolved requesterType:', requesterType);
      console.log('  ΓåÆ All user state:', state.user);

      // ALWAYS set RequesterType header (for both Internal and External users)
      // Backend defaults to External if header is missing
      if (requesterType) {
        headers.set('RequesterType', requesterType);
        console.log('[auth.baseQuery] Γ£à RequesterType header SET to:', requesterType);
      } else {
        console.warn('[auth.baseQuery] Γ¥î RequesterType header NOT set ΓÇö requesterType is null/undefined');
      }
    }
    return headers;
  },
});

const baseQueryWithMiddleware: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    window.location.reload();
  }
  return result;
};

export {baseQueryWithMiddleware, baseQuery};
